// app/ningenie/hooks/use-ningenie-snapshot.ts
// baby: React Query hook for NinGenie snapshot data with real-time Firebase subscriptions

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { auth, onAuthStateChanged } from '@/app/lib/firebase'
import { getNinGenieUserId } from '../lib/ningenie-user'
import { fetchNinGenieSnapshot, subscribeToNinGenieSnapshot } from '../lib/ningenie-repository'
import type { NinGenieSnapshot } from '../lib/ningenie-types'

export const useNinGenieSnapshot = () => {
  const queryClient = useQueryClient()
  // baby: track user ID in state so it updates when auth changes
  const [userId, setUserId] = useState(() => getNinGenieUserId())
  
  // baby: update user ID when auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      setUserId(getNinGenieUserId())
    })
    return unsubscribe
  }, [])
  
  // baby: query key includes user ID to ensure refetch when user changes
  // baby: memoize queryKey to prevent unnecessary re-renders and subscription restarts
  const queryKey = useMemo(() => ['ningenie', 'snapshot', userId], [userId])
  
  // baby: memoize subscription callbacks to prevent unnecessary re-subscriptions
  const handleSnapshotUpdate = useCallback((snapshot: NinGenieSnapshot) => {
    console.log('[useNinGenieSnapshot] Received real-time update, updating cache')
    // baby: update React Query cache with new data
    queryClient.setQueryData(queryKey, snapshot)
  }, [queryClient, queryKey])
  
  const handleSubscriptionError = useCallback((error: Error) => {
    console.error('[useNinGenieSnapshot] Subscription error:', error)
  }, [])
  
  // baby: set up real-time subscription to automatically update React Query cache
  useEffect(() => {
    // baby: skip subscription if userId is not available
    if (!userId) {
      return
    }
    
    console.log('[useNinGenieSnapshot] Setting up real-time subscription for userId:', userId)
    
    // baby: subscribe to real-time updates and update the cache
    const unsubscribe = subscribeToNinGenieSnapshot(
      handleSnapshotUpdate,
      handleSubscriptionError
    )
    
    // baby: cleanup subscription when component unmounts or userId changes
    return () => {
      console.log('[useNinGenieSnapshot] Cleaning up subscription')
      unsubscribe()
    }
  }, [userId, handleSnapshotUpdate, handleSubscriptionError])
  
  // baby: use React Query for initial fetch and caching
  return useQuery({
    queryKey,
    queryFn: fetchNinGenieSnapshot,
    staleTime: Number.POSITIVE_INFINITY, // baby: never stale - we use real-time updates
    gcTime: 1000 * 60 * 5 // 5 minutes
  })
}

export const useInvalidateNinGenieSnapshot = () => {
  const queryClient = useQueryClient()
  return () => {
    const userId = getNinGenieUserId()
    queryClient.invalidateQueries({ queryKey: ['ningenie', 'snapshot', userId] })
    // baby: also invalidate any query key pattern to catch all user data
    queryClient.invalidateQueries({ queryKey: ['ningenie', 'snapshot'] })
  }
}
